import {
  AdditiveBlending,
  BoxGeometry,
  CanvasTexture,
  ConeGeometry,
  CylinderGeometry,
  Euler,
  Group,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  Quaternion,
  SphereGeometry,
  SRGBColorSpace,
  Vector3,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const UNIT_SCALE = new Vector3(1, 1, 1);
const IDENTITY = new Quaternion();

/**
 * Materials, procedural decals and merged low-poly templates for the police
 * checkpoints. Trucks and officers are built once and cloned, so the four
 * checkpoints stay cheap to draw.
 */
export function createPoliceAssets() {
  const doorTexture = createDoorTexture();
  const hoodTexture = createHoodTexture();
  const tailgateTexture = createTailgateTexture();
  const checkpointTexture = createCheckpointSignTexture();
  const stopTexture = createStopTexture();
  const stripeTexture = createBarrierStripeTexture();

  const materials = {
    body: new MeshStandardMaterial({
      color: 0xf0eee6,
      roughness: 0.4,
      metalness: 0.12,
    }),
    livery: new MeshStandardMaterial({
      color: 0x14518c,
      roughness: 0.46,
      metalness: 0.12,
    }),
    dark: new MeshStandardMaterial({
      color: 0x222a2c,
      roughness: 0.62,
      metalness: 0.24,
    }),
    gunmetal: new MeshStandardMaterial({
      color: 0x3c4140,
      roughness: 0.46,
      metalness: 0.68,
    }),
    gunSteel: new MeshStandardMaterial({
      color: 0x777d78,
      roughness: 0.34,
      metalness: 0.82,
    }),
    olive: new MeshStandardMaterial({
      color: 0x4d5638,
      roughness: 0.84,
      metalness: 0.06,
    }),
    brass: new MeshStandardMaterial({
      color: 0xb18a3f,
      roughness: 0.36,
      metalness: 0.72,
    }),
    chrome: new MeshStandardMaterial({
      color: 0xb9bdb8,
      roughness: 0.28,
      metalness: 0.78,
    }),
    glass: new MeshStandardMaterial({
      color: 0x1a282f,
      roughness: 0.15,
      metalness: 0.5,
    }),
    tire: new MeshStandardMaterial({
      color: 0x151617,
      roughness: 0.92,
      metalness: 0.01,
    }),
    headlight: new MeshBasicMaterial({
      color: 0xfff1c8,
      toneMapped: false,
    }),
    taillight: new MeshBasicMaterial({
      color: 0xa1241e,
      toneMapped: false,
    }),
    blueLens: new MeshBasicMaterial({
      color: 0x2d63ff,
      toneMapped: false,
    }),
    redLens: new MeshBasicMaterial({
      color: 0xff3044,
      toneMapped: false,
    }),
    whiteLens: new MeshBasicMaterial({
      color: 0xdde8ff,
      toneMapped: false,
    }),
    amber: new MeshBasicMaterial({
      color: 0xffa323,
      toneMapped: false,
    }),
    beacon: new MeshBasicMaterial({
      color: 0xffa323,
      toneMapped: false,
    }),
    reflective: new MeshBasicMaterial({
      color: 0xf5d34e,
      toneMapped: false,
    }),
    stripe: new MeshStandardMaterial({
      map: stripeTexture,
      roughness: 0.68,
      metalness: 0.04,
    }),
    cone: new MeshStandardMaterial({
      color: 0xd95721,
      roughness: 0.74,
    }),
    coneBand: new MeshStandardMaterial({
      color: 0xf0eadb,
      roughness: 0.62,
    }),
    muzzleFlash: new MeshBasicMaterial({
      color: 0xffd477,
      transparent: true,
      opacity: 0.98,
      depthWrite: false,
      toneMapped: false,
      blending: AdditiveBlending,
    }),
    tracer: new MeshBasicMaterial({
      color: 0xffc45a,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      toneMapped: false,
      blending: AdditiveBlending,
    }),
    shirt: new MeshStandardMaterial({
      color: 0x86a9bf,
      roughness: 0.9,
    }),
    trousers: new MeshStandardMaterial({
      color: 0x26364d,
      roughness: 0.94,
    }),
    skin: new MeshStandardMaterial({
      color: 0x684936,
      roughness: 0.92,
    }),
    glove: new MeshStandardMaterial({
      color: 0xe7e5dc,
      roughness: 0.82,
    }),
    silver: new MeshStandardMaterial({
      color: 0xc3c6c1,
      roughness: 0.32,
      metalness: 0.66,
    }),
    gold: new MeshStandardMaterial({
      color: 0xc7a34b,
      roughness: 0.4,
      metalness: 0.5,
    }),
    doorDecal: new MeshBasicMaterial({
      map: doorTexture,
      transparent: true,
      depthWrite: false,
      toneMapped: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    hoodDecal: new MeshBasicMaterial({
      map: hoodTexture,
      transparent: true,
      depthWrite: false,
      toneMapped: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    tailgateDecal: new MeshBasicMaterial({
      map: tailgateTexture,
      transparent: true,
      depthWrite: false,
      toneMapped: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    checkpointSign: new MeshBasicMaterial({
      map: checkpointTexture,
      toneMapped: false,
    }),
    stopSign: new MeshBasicMaterial({
      map: stopTexture,
      transparent: true,
      alphaTest: 0.08,
      depthWrite: false,
      toneMapped: false,
    }),
  };

  return {
    materials,
    templates: {
      truck: createPoliceTruck(materials),
      armedTruck: createPoliceTruck(materials, { armed: true }),
      barricade: createBarricade(materials),
      cone: createCone(materials),
      sign: createCheckpointSign(materials),
      director: createOfficer(materials, 'direct'),
      standing: createOfficer(materials, 'stand'),
    },
  };
}

function createPoliceTruck(materials, { armed = false } = {}) {
  const group = new Group();
  const buckets = new Map();
  const addBox = (material, size, position, rotation = null) => {
    addGeometry(
      buckets,
      material,
      new BoxGeometry(size[0], size[1], size[2]),
      new Vector3(...position),
      rotation,
    );
  };
  const addCylinder = (material, geometry, position, rotation = null) => {
    addGeometry(buckets, material, geometry, new Vector3(...position), rotation);
  };
  // Chassis, lower body and the royal-blue police livery band.
  addBox(materials.dark, [1.55, 0.18, 4.95], [0, 0.5, 0]);
  addBox(materials.body, [1.88, 0.55, 4.95], [0, 0.86, 0]);
  addBox(materials.livery, [1.9, 0.16, 4.8], [0, 0.59, 0]);
  addBox(materials.body, [1.86, 0.2, 1.45], [0, 1.18, 1.68]);

  // Front end: grille, bumper, lights, plate and the heavy push bar.
  addBox(materials.dark, [1.22, 0.26, 0.06], [0, 1.02, 2.46]);
  for (let i = 0; i < 3; i += 1) {
    addBox(materials.chrome, [1.04, 0.025, 0.08], [0, 0.94 + i * 0.07, 2.49]);
  }
  addBox(materials.dark, [2.0, 0.22, 0.3], [0, 0.68, 2.52]);
  addBox(materials.chrome, [1.1, 0.12, 0.31], [0, 0.68, 2.53]);
  for (const x of [-0.63, 0.63]) {
    addBox(materials.dark, [0.4, 0.22, 0.05], [x, 1.03, 2.49]);
    addBox(materials.headlight, [0.34, 0.16, 0.06], [x, 1.03, 2.51]);
    addBox(materials.amber, [0.18, 0.07, 0.06], [x, 0.87, 2.51]);
  }
  addBox(materials.body, [0.34, 0.17, 0.02], [0, 0.78, 2.67]);
  addBox(materials.dark, [0.08, 0.72, 0.08], [-0.56, 1.02, 2.66]);
  addBox(materials.dark, [0.08, 0.72, 0.08], [0.56, 1.02, 2.66]);
  addBox(materials.dark, [1.26, 0.08, 0.08], [0, 1.3, 2.66]);
  addBox(materials.dark, [1.26, 0.07, 0.08], [0, 0.8, 2.66]);

  // Double cab. Glass panels sit over the body and are framed by the roof,
  // pillars and the white lower door panels.
  addBox(materials.body, [1.82, 0.62, 2.35], [0, 1.43, 0.35]);
  addBox(materials.body, [1.76, 0.1, 2.13], [0, 1.99, 0.27]);
  addBox(materials.body, [1.78, 0.08, 0.16], [0, 1.94, 1.34]);
  for (const x of [-0.918, 0.918]) {
    addBox(materials.glass, [0.035, 0.51, 2.02], [x, 1.67, 0.27]);
    addBox(materials.body, [0.05, 0.55, 0.09], [x, 1.66, 0.26]);
    addBox(materials.body, [0.05, 0.55, 0.09], [x, 1.66, 1.37]);
    addBox(materials.body, [0.05, 0.55, 0.09], [x, 1.66, -0.84]);
    addBox(materials.chrome, [0.16, 0.045, 0.045], [x, 1.25, 0.46]);
    addBox(materials.chrome, [0.16, 0.045, 0.045], [x, 1.25, -0.42]);
    addBox(materials.dark, [0.06, 0.14, 0.12], [x * 1.1, 1.6, 1.25]);
    addBox(materials.chrome, [0.07, 0.025, 0.17], [x * 1.16, 1.62, 1.25]);
  }
  addBox(materials.glass, [1.58, 0.5, 0.045], [0, 1.67, 1.505]);
  addBox(materials.glass, [1.55, 0.43, 0.04], [0, 1.68, -0.835]);

  // Bed, tailgate and police equipment in the back.
  addBox(materials.dark, [1.68, 0.08, 1.88], [0, 1.02, -1.73]);
  for (const x of [-0.91, 0.91]) {
    addBox(materials.body, [0.08, 0.5, 1.86], [x, 1.26, -1.73]);
    addBox(materials.livery, [0.095, 0.06, 1.9], [x, 1.53, -1.73]);
  }
  addBox(materials.body, [1.84, 0.5, 0.08], [0, 1.26, -0.82]);
  addBox(materials.body, [1.84, 0.5, 0.08], [0, 1.26, -2.61]);
  addBox(materials.dark, [1.9, 0.16, 0.22], [0, 0.64, -2.68]);
  addBox(materials.silver, [0.32, 0.16, 0.02], [0, 0.98, -2.66]);

  if (!armed) {
    const spareTire = new CylinderGeometry(0.33, 0.33, 0.18, 16);
    addCylinder(materials.tire, spareTire, [0, 1.15, -1.72]);
    addCylinder(
      materials.silver,
      new CylinderGeometry(0.15, 0.15, 0.19, 12),
      [0, 1.16, -1.72],
    );
    addBox(materials.dark, [0.72, 0.28, 0.44], [-0.28, 1.25, -1.35]);
    addBox(materials.gold, [0.08, 0.04, 0.05], [-0.06, 1.23, -1.13]);
    addBox(materials.livery, [0.48, 0.22, 0.32], [0.48, 1.24, -1.4]);
    addBox(materials.dark, [0.05, 0.22, 0.05], [0.48, 1.44, -1.52]);
  }

  // Wheels, hubs, fender flares and side steps.
  const tireGeometry = new CylinderGeometry(0.4, 0.4, 0.28, 16);
  tireGeometry.rotateZ(Math.PI / 2);
  const hubGeometry = new CylinderGeometry(0.2, 0.2, 0.29, 12);
  hubGeometry.rotateZ(Math.PI / 2);
  const hubCapGeometry = new CylinderGeometry(0.07, 0.07, 0.3, 10);
  hubCapGeometry.rotateZ(Math.PI / 2);
  for (const x of [-0.9, 0.9]) {
    for (const z of [1.63, -1.62]) {
      addCylinder(materials.tire, tireGeometry.clone(), [x, 0.4, z]);
      addCylinder(materials.silver, hubGeometry.clone(), [x, 0.4, z]);
      addCylinder(materials.dark, hubCapGeometry.clone(), [x, 0.4, z]);
      addBox(materials.dark, [0.05, 0.14, 0.96], [x * 1.06, 0.73, z]);
    }
    addBox(materials.chrome, [0.11, 0.055, 1.92], [x * 1.06, 0.55, 0.05]);
  }

  // Roof lightbar and radio antennas.
  addBox(materials.dark, [1.34, 0.09, 0.3], [0, 2.08, 0.35]);
  addBox(materials.dark, [0.16, 0.04, 0.22], [-0.61, 2.07, 0.35]);
  addBox(materials.dark, [0.16, 0.04, 0.22], [0.61, 2.07, 0.35]);
  addCylinder(
    materials.dark,
    new CylinderGeometry(0.012, 0.012, 0.95, 5),
    [-0.72, 2.4, -0.55],
  );
  addCylinder(
    materials.dark,
    new CylinderGeometry(0.013, 0.013, 0.7, 5),
    [0.72, 2.27, -0.58],
  );

  flushBuckets(group, buckets);

  // Lightbar lenses stay separate because their materials are animated.
  const blueLens = new Mesh(new BoxGeometry(0.5, 0.14, 0.24), materials.blueLens);
  blueLens.position.set(-0.36, 2.16, 0.35);
  const redLens = new Mesh(new BoxGeometry(0.5, 0.14, 0.24), materials.redLens);
  redLens.position.set(0.36, 2.16, 0.35);
  const whiteLens = new Mesh(new BoxGeometry(0.18, 0.1, 0.24), materials.whiteLens);
  whiteLens.position.set(0, 2.16, 0.35);
  group.add(blueLens, redLens, whiteLens);

  // Side and hood markings.
  const sideGeometry = new PlaneGeometry(1.5, 0.42);
  for (const side of [-1, 1]) {
    const decal = new Mesh(sideGeometry, materials.doorDecal);
    decal.position.set(side * 0.925, 1.32, 0.32);
    decal.rotation.y = side > 0 ? Math.PI / 2 : -Math.PI / 2;
    group.add(decal);
  }

  const hoodDecal = new Mesh(new PlaneGeometry(1.25, 0.5), materials.hoodDecal);
  hoodDecal.position.set(0, 1.291, 1.65);
  hoodDecal.rotation.x = -Math.PI / 2;
  group.add(hoodDecal);

  const tailDecal = new Mesh(new PlaneGeometry(1.45, 0.32), materials.tailgateDecal);
  tailDecal.position.set(0, 1.27, -2.655);
  tailDecal.rotation.y = Math.PI;
  group.add(tailDecal);

  if (armed) {
    group.add(createDshk(materials));

    const gunner = createOfficer(materials, 'gunner');
    gunner.position.set(0, 1.03, -2.32);
    group.add(gunner);
  }

  group.traverse((child) => {
    if (!(child instanceof Mesh)) return;
    const unlit = child.material === materials.doorDecal
      || child.material === materials.hoodDecal
      || child.material === materials.tailgateDecal
      || child.material === materials.muzzleFlash;
    child.castShadow = !unlit;
    child.receiveShadow = !unlit;
  });
  return group;
}

/**
 * Pedestal-mounted DShK heavy machine gun for the truck bed. The mount is
 * fixed to the bed while the receiver and barrel sit on a yaw/elevation
 * cradle, allowing the gunner to track a vehicle across the checkpoint
 * approach.
 */
function createDshk(materials) {
  const group = new Group();
  const mountBuckets = new Map();
  const gunBuckets = new Map();
  const pivot = new Vector3(0, 2.46, -1.3);

  const addMount = (material, geometry, position) => {
    addGeometry(mountBuckets, material, geometry, new Vector3(...position));
  };
  const addGun = (material, geometry, position) => {
    addGeometry(
      gunBuckets,
      material,
      geometry,
      new Vector3(...position).sub(pivot),
    );
  };
  const mountBox = (material, size, position) => {
    addMount(material, new BoxGeometry(...size), position);
  };
  const mountCylinderY = (material, radius, height, position) => {
    addMount(
      material,
      new CylinderGeometry(radius, radius, height, 10),
      position,
    );
  };
  const gunBox = (material, size, position) => {
    addGun(material, new BoxGeometry(...size), position);
  };
  const gunCylinderZ = (material, radiusTop, radiusBottom, length, position) => {
    const geometry = new CylinderGeometry(radiusTop, radiusBottom, length, 10);
    geometry.rotateX(Math.PI / 2);
    addGun(material, geometry, position);
  };

  // Pedestal, collar, trunnion and the fixed half of the pintle mount.
  mountBox(materials.gunmetal, [0.58, 0.06, 0.58], [0, 1.1, -1.3]);
  mountCylinderY(materials.gunmetal, 0.085, 1.16, [0, 1.69, -1.3]);
  mountCylinderY(materials.gunmetal, 0.15, 0.1, [0, 2.28, -1.3]);
  mountBox(materials.gunmetal, [0.055, 0.36, 0.16], [-0.18, 2.38, -1.3]);
  mountBox(materials.gunmetal, [0.055, 0.36, 0.16], [0.18, 2.38, -1.3]);
  const trunnion = new CylinderGeometry(0.04, 0.04, 0.47, 10);
  trunnion.rotateZ(Math.PI / 2);
  addMount(materials.gunSteel, trunnion, [0, 2.46, -1.3]);

  const aim = new Group();
  aim.name = 'dshk-aim';
  aim.position.copy(pivot);
  const elevation = new Group();
  elevation.name = 'dshk-elevation';
  aim.add(elevation);

  // Boxy 12.7 mm receiver, top cover and feed tray.
  gunBox(materials.gunmetal, [0.28, 0.25, 0.68], [0, 2.45, -1.21]);
  gunBox(materials.gunmetal, [0.23, 0.06, 0.5], [0, 2.59, -1.2]);
  gunBox(materials.gunmetal, [0.3, 0.18, 0.1], [0, 2.42, -1.59]);
  gunBox(materials.gunSteel, [0.31, 0.05, 0.27], [0, 2.6, -1.12]);
  gunBox(materials.gunSteel, [0.04, 0.14, 0.18], [0.18, 2.44, -1.02]);

  // Ribbed barrel jacket, long barrel and DShK muzzle brake.
  gunCylinderZ(materials.gunmetal, 0.07, 0.07, 0.52, [0, 2.46, -0.57]);
  for (let i = 0; i < 5; i += 1) {
    gunCylinderZ(
      materials.gunSteel,
      0.078,
      0.078,
      0.028,
      [0, 2.46, -0.78 + i * 0.1],
    );
  }
  gunCylinderZ(materials.gunmetal, 0.038, 0.042, 0.8, [0, 2.46, 0.13]);
  gunCylinderZ(materials.gunSteel, 0.075, 0.055, 0.18, [0, 2.46, 0.58]);
  for (let i = 0; i < 3; i += 1) {
    gunCylinderZ(
      materials.gunSteel,
      0.094,
      0.094,
      0.026,
      [0, 2.46, 0.51 + i * 0.07],
    );
  }
  gunBox(materials.gunSteel, [0.025, 0.11, 0.035], [0, 2.56, 0.45]);
  gunBox(materials.gunSteel, [0.035, 0.09, 0.15], [0, 2.62, -1.01]);

  // Ammunition box and a brass belt feeding into the receiver.
  gunBox(materials.olive, [0.32, 0.28, 0.42], [0.25, 2.33, -1.14]);
  gunBox(materials.olive, [0.34, 0.04, 0.44], [0.25, 2.49, -1.14]);
  gunBox(materials.silver, [0.04, 0.025, 0.19], [0.4, 2.5, -1.14]);
  for (let i = 0; i < 4; i += 1) {
    gunBox(
      materials.brass,
      [0.027, 0.027, 0.105],
      [0.12, 2.61, -1.31 + i * 0.1],
    );
  }

  // Rear spade grips and trigger bar for the gunner.
  gunBox(materials.gunSteel, [0.34, 0.03, 0.045], [0, 2.36, -1.68]);
  gunCylinderZ(materials.tire, 0.025, 0.025, 0.28, [-0.2, 2.36, -1.84]);
  gunCylinderZ(materials.tire, 0.025, 0.025, 0.28, [0.2, 2.36, -1.84]);
  gunBox(materials.gunmetal, [0.08, 0.11, 0.06], [0, 2.34, -1.72]);

  flushBuckets(elevation, gunBuckets);
  elevation.add(createDshkMuzzleFlash(materials));
  group.add(aim);
  flushBuckets(group, mountBuckets);
  return group;
}

/** Additive muzzle flash that travels with the DShK's elevation cradle. */
function createDshkMuzzleFlash(materials) {
  const flash = new Group();
  flash.name = 'dshk-muzzle-flash';
  flash.position.set(0, 0, 1.97);
  flash.visible = false;

  const core = new Mesh(
    new SphereGeometry(0.1, 6, 5),
    materials.muzzleFlash,
  );
  core.scale.set(1.15, 0.9, 2.35);

  const jet = new Mesh(
    new ConeGeometry(0.115, 0.55, 7),
    materials.muzzleFlash,
  );
  jet.rotation.x = Math.PI / 2;
  jet.position.z = 0.24;

  const crossH = new Mesh(
    new BoxGeometry(0.62, 0.055, 0.055),
    materials.muzzleFlash,
  );
  crossH.position.z = 0.15;
  const crossV = new Mesh(
    new BoxGeometry(0.055, 0.62, 0.055),
    materials.muzzleFlash,
  );
  crossV.position.z = 0.15;

  flash.add(core, jet, crossH, crossV);
  flash.traverse((child) => {
    if (!(child instanceof Mesh)) return;
    child.castShadow = false;
    child.receiveShadow = false;
    child.frustumCulled = false;
  });
  return flash;
}

function createOfficer(materials, pose) {
  const group = new Group();
  const buckets = new Map();
  const addBox = (material, size, position, rotation = null) => {
    addGeometry(
      buckets,
      material,
      new BoxGeometry(size[0], size[1], size[2]),
      new Vector3(...position),
      rotation,
    );
  };
  const addSphere = (material, radius, position, scale = null) => {
    addGeometry(
      buckets,
      material,
      new SphereGeometry(radius, 10, 8),
      new Vector3(...position),
      null,
      scale,
    );
  };

  // Black boots, navy trousers and the light-blue khaki uniform shirt.
  for (const side of [-1, 1]) {
    addBox(materials.dark, [0.18, 0.17, 0.34], [side * 0.115, 0.085, 0.05]);
    addBox(materials.trousers, [0.18, 0.36, 0.21], [side * 0.115, 0.35, 0]);
    addBox(materials.trousers, [0.2, 0.36, 0.24], [side * 0.115, 0.68, 0]);
    addBox(materials.dark, [0.19, 0.055, 0.26], [side * 0.23, 1.42, 0]);
    addBox(materials.silver, [0.055, 0.026, 0.026], [side * 0.23, 1.46, 0.09]);
  }
  addBox(materials.trousers, [0.43, 0.18, 0.27], [0, 0.86, 0]);
  addBox(materials.dark, [0.49, 0.08, 0.29], [0, 0.94, 0]);
  addBox(materials.silver, [0.08, 0.06, 0.025], [0, 0.94, 0.15]);
  addBox(materials.shirt, [0.47, 0.5, 0.27], [0, 1.17, 0]);
  addBox(materials.shirt, [0.1, 0.42, 0.03], [0, 1.17, 0.145]);
  for (let i = 0; i < 4; i += 1) {
    addBox(materials.dark, [0.025, 0.025, 0.022], [0, 1.3 - i * 0.1, 0.166]);
  }
  addBox(materials.silver, [0.06, 0.07, 0.018], [-0.13, 1.3, 0.148]);
  addBox(materials.gold, [0.044, 0.05, 0.02], [-0.13, 1.3, 0.159]);
  addBox(materials.dark, [0.085, 0.17, 0.06], [0.29, 0.82, 0.04]);
  addBox(materials.dark, [0.07, 0.13, 0.05], [-0.17, 1.32, 0.15]);
  addBox(materials.dark, [0.02, 0.18, 0.02], [-0.2, 1.47, 0.15]);
  addBox(materials.dark, [0.18, 0.28, 0.14], [0, 1.53, -0.02]);
  addGeometry(
    buckets,
    materials.skin,
    new CylinderGeometry(0.065, 0.075, 0.11, 8),
    new Vector3(0, 1.53, 0),
  );
  addSphere(materials.skin, 0.13, [0, 1.65, 0.01], [0.93, 1.03, 0.97]);
  addGeometry(
    buckets,
    materials.dark,
    new CylinderGeometry(0.15, 0.15, 0.06, 12),
    new Vector3(0, 1.76, 0),
  );
  addSphere(materials.dark, 0.16, [0, 1.79, 0], [1.1, 0.46, 1.1]);
  addBox(materials.dark, [0.27, 0.025, 0.13], [0, 1.73, 0.12]);
  addBox(materials.gold, [0.05, 0.065, 0.02], [0, 1.76, 0.155]);

  // Arms use shoulder pivots. The director's raised white-gloved arm is the
  // clearest read of a traffic police checkpoint; the gunner reaches forward
  // with both hands to the DShK spade grips.
  for (const side of [-1, 1]) {
    const raised = pose === 'direct' && side > 0;
    const relaxed = pose === 'stand' && side < 0;
    const gunner = pose === 'gunner';
    let armPitch = side > 0 ? -0.08 : 0.08;
    let armYaw = 0;
    let armRoll = side * 0.06;
    if (raised) {
      armPitch = -2.35;
      armRoll = side * -0.12;
    } else if (gunner) {
      armPitch = -1.42;
      armYaw = -side * 0.18;
      armRoll = -side * 0.24;
    } else if (relaxed) {
      armPitch = -0.55;
    }
    const rotation = new Quaternion().setFromEuler(
      new Euler(armPitch, armYaw, armRoll),
    );
    const pivot = new Vector3(side * 0.3, 1.4, 0);
    const armPart = (material, size, localY) => {
      const local = new Vector3(0, localY, 0).applyQuaternion(rotation).add(pivot);
      addGeometry(
        buckets,
        material,
        new BoxGeometry(size[0], size[1], size[2]),
        local,
        rotation,
      );
    };
    armPart(materials.shirt, [0.15, 0.3, 0.18], -0.14);
    armPart(materials.shirt, [0.13, 0.28, 0.15], -0.41);
    const hand = new Vector3(0, -0.6, 0).applyQuaternion(rotation).add(pivot);
    addSphere(
      raised ? materials.glove : materials.skin,
      0.07,
      [hand.x, hand.y, hand.z],
    );
  }

  flushBuckets(group, buckets);
  return group;
}

function createBarricade(materials) {
  const group = new Group();
  const buckets = new Map();
  const width = 2.36;
  for (const side of [-1, 1]) {
    const x = side * (width / 2 - 0.055);
    addGeometry(
      buckets,
      materials.dark,
      new BoxGeometry(0.09, 1.12, 0.09),
      new Vector3(x, 0.56, 0),
    );
    addGeometry(
      buckets,
      materials.dark,
      new BoxGeometry(0.6, 0.07, 0.48),
      new Vector3(x, 0.035, 0),
    );
    addGeometry(
      buckets,
      materials.reflective,
      new BoxGeometry(0.17, 0.07, 0.02),
      new Vector3(x, 0.82, 0.06),
    );
  }
  for (const [y, height] of [[1.02, 0.2], [0.58, 0.16]]) {
    addGeometry(
      buckets,
      materials.stripe,
      new BoxGeometry(width, height, 0.12),
      new Vector3(0, y, 0),
    );
  }
  flushBuckets(group, buckets);
  return group;
}

function createCone(materials) {
  const group = new Group();
  const base = new Mesh(new BoxGeometry(0.38, 0.04, 0.38), materials.dark);
  base.position.y = 0.02;
  const cone = new Mesh(new ConeGeometry(0.17, 0.6, 10), materials.cone);
  cone.position.y = 0.32;
  const band = new Mesh(
    new CylinderGeometry(0.105, 0.13, 0.09, 10),
    materials.coneBand,
  );
  band.position.y = 0.34;
  group.add(base, cone, band);
  group.traverse((child) => {
    if (child instanceof Mesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
  return group;
}

function createCheckpointSign(materials) {
  const group = new Group();
  const buckets = new Map();
  for (const side of [-1, 1]) {
    addGeometry(
      buckets,
      materials.dark,
      new BoxGeometry(0.09, 3.2, 0.09),
      new Vector3(side * 1.55, 1.6, -0.05),
    );
    addGeometry(
      buckets,
      materials.dark,
      new BoxGeometry(0.42, 0.06, 0.42),
      new Vector3(side * 1.55, 0.03, -0.05),
    );
  }
  addGeometry(
    buckets,
    materials.livery,
    new BoxGeometry(3.96, 1.48, 0.08),
    new Vector3(0, 2.7, -0.03),
  );
  flushBuckets(group, buckets);

  const board = new Mesh(
    new PlaneGeometry(3.82, 1.34),
    materials.checkpointSign,
  );
  board.position.set(0, 2.7, 0.018);
  group.add(board);
  return group;
}

function flushBuckets(parent, buckets, { castShadow = true, receiveShadow = true } = {}) {
  for (const [material, geometries] of buckets) {
    if (!geometries.length) continue;
    const merged = mergeGeometries(geometries, false);
    for (const geometry of geometries) geometry.dispose();
    if (!merged) continue;
    const mesh = new Mesh(merged, material);
    mesh.castShadow = castShadow;
    mesh.receiveShadow = receiveShadow;
    parent.add(mesh);
  }
}

function addGeometry(
  buckets,
  material,
  geometry,
  position,
  rotation = null,
  scale = null,
) {
  const quaternion = rotation instanceof Quaternion
    ? rotation
    : rotation
      ? new Quaternion().setFromEuler(new Euler(...rotation))
      : IDENTITY;
  const matrix = new Matrix4().compose(
    position,
    quaternion,
    scale ? new Vector3(...scale) : UNIT_SCALE,
  );
  geometry.applyMatrix4(matrix);
  if (!buckets.has(material)) buckets.set(material, []);
  buckets.get(material).push(geometry);
}

function createCanvasTexture(width, height, draw) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  draw(ctx, width, height);
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 8;
  texture.needsUpdate = true;
  return texture;
}

function drawSudanFlag(ctx, x, y, width, height) {
  ctx.save();
  ctx.fillStyle = '#d21034';
  ctx.fillRect(x, y, width, height / 3);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x, y + height / 3, width, height / 3);
  ctx.fillStyle = '#111111';
  ctx.fillRect(x, y + (height * 2) / 3, width, height / 3);
  ctx.fillStyle = '#007229';
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + width * 0.36, y + height / 2);
  ctx.lineTo(x, y + height);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function createDoorTexture() {
  return createCanvasTexture(640, 256, (ctx, width, height) => {
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#124b85';
    ctx.beginPath();
    ctx.roundRect(5, 5, width - 10, height - 10, 24);
    ctx.fill();
    ctx.strokeStyle = '#e3bd57';
    ctx.lineWidth = 7;
    ctx.stroke();
    drawSudanFlag(ctx, 26, 28, 96, 62);
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.direction = 'rtl';
    ctx.font = '700 76px "Geeza Pro", "Al Nile", "Noto Naskh Arabic", Arial, sans-serif';
    ctx.fillText('شرطة السودان', width / 2, 107);
    ctx.direction = 'ltr';
    ctx.fillStyle = '#f0cf79';
    ctx.font = '700 42px "Avenir Next", "Helvetica Neue", Arial, sans-serif';
    ctx.fillText('SUDAN POLICE', width / 2, 181);
    ctx.fillStyle = '#e3bd57';
    ctx.fillRect(150, height - 30, width - 300, 5);
  });
}

function createHoodTexture() {
  return createCanvasTexture(640, 280, (ctx, width, height) => {
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#124b85';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.direction = 'ltr';
    ctx.font = '800 132px "Avenir Next", "Helvetica Neue", Arial, sans-serif';
    ctx.fillText('POLICE', width / 2, height / 2 - 12);
    ctx.fillStyle = '#dfbb59';
    ctx.font = '700 34px "Geeza Pro", "Arial", sans-serif';
    ctx.direction = 'rtl';
    ctx.fillText('شرطة السودان', width / 2, height - 30);
  });
}

function createTailgateTexture() {
  return createCanvasTexture(640, 160, (ctx, width, height) => {
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#124b85';
    ctx.beginPath();
    ctx.roundRect(4, 4, width - 8, height - 8, 12);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.direction = 'rtl';
    ctx.font = '700 72px "Geeza Pro", "Al Nile", "Arial", sans-serif';
    ctx.fillText('شرطة', width / 2 - 120, height / 2);
    ctx.direction = 'ltr';
    ctx.font = '800 58px "Avenir Next", "Arial", sans-serif';
    ctx.fillStyle = '#f0cf79';
    ctx.fillText('POLICE', width / 2 + 115, height / 2);
  });
}

function createCheckpointSignTexture() {
  return createCanvasTexture(1200, 460, (ctx, width, height) => {
    ctx.fillStyle = '#0e3e70';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#f4f0e5';
    ctx.lineWidth = 14;
    ctx.strokeRect(17, 17, width - 34, height - 34);
    ctx.strokeStyle = '#d6b151';
    ctx.lineWidth = 5;
    ctx.strokeRect(36, 36, width - 72, height - 72);
    drawSudanFlag(ctx, 58, 58, 154, 100);
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.direction = 'rtl';
    ctx.font = '700 104px "Geeza Pro", "Al Nile", "Noto Naskh Arabic", Arial, sans-serif';
    ctx.fillText('شرطة السودان', width / 2, 145);
    ctx.direction = 'ltr';
    ctx.fillStyle = '#f1ce73';
    ctx.font = '800 48px "Avenir Next", "Helvetica Neue", Arial, sans-serif';
    ctx.fillText('SUDAN POLICE', width / 2, 230);
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 38px "Avenir Next", "Arial", sans-serif';
    ctx.direction = 'ltr';
    ctx.fillText('POLICE CHECKPOINT', width / 2, 300);
    ctx.direction = 'rtl';
    ctx.font = '600 34px "Geeza Pro", "Arial", sans-serif';
    ctx.fillText('نقطة تفتيش', width / 2, 352);
    ctx.fillStyle = 'rgba(255,255,255,0.74)';
    ctx.font = '500 26px "Avenir Next", "Arial", sans-serif';
    ctx.direction = 'ltr';
    ctx.fillText('BASHIR ELNIFIDI STREET · STREET 60', width / 2, 408);
  });
}

function createStopTexture() {
  return createCanvasTexture(512, 512, (ctx, width, height) => {
    ctx.clearRect(0, 0, width, height);
    ctx.translate(width / 2, height / 2);
    ctx.fillStyle = '#c82f2b';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 20;
    ctx.beginPath();
    for (let i = 0; i < 8; i += 1) {
      const angle = Math.PI / 8 + (i * Math.PI) / 4;
      const x = Math.cos(angle) * 222;
      const y = Math.sin(angle) * 222;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.direction = 'rtl';
    ctx.font = '800 160px "Geeza Pro", "Al Nile", "Arial", sans-serif';
    ctx.fillText('قف', 0, -38);
    ctx.direction = 'ltr';
    ctx.font = '800 70px "Avenir Next", "Arial", sans-serif';
    ctx.fillText('STOP', 0, 104);
  });
}

function createBarrierStripeTexture() {
  return createCanvasTexture(512, 128, (ctx, width, height) => {
    ctx.fillStyle = '#eee9dc';
    ctx.fillRect(0, 0, width, height);
    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.rotate(-Math.PI / 4);
    ctx.fillStyle = '#c43b32';
    for (let x = -width; x < width; x += 92) {
      ctx.fillRect(x, -height, 40, height * 2);
    }
    ctx.restore();
    ctx.strokeStyle = 'rgba(40, 38, 34, 0.24)';
    ctx.lineWidth = 6;
    ctx.strokeRect(3, 3, width - 6, height - 6);
  });
}
